import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { AssistantInputArea, AssistantInputAreaRef } from "./AssistantInputArea";
import { AgentService } from "../../services/AgentService";
import { ApplicationService } from "../../services/ApplicationService";
import { usePermissions } from "../../PermissionsProvider";
import { useAssistant } from "./AssistantContext";
import { Button } from "../ui/button";
import DiffView from "../ui/DiffView";
import { InlineSuggestions } from "./InlineSuggestions";
import { MessageBubble } from "./MessageBubble";
import { EmptyState } from "./EmptyState";
import { LoadingIndicator } from "./LoadingIndicator";
import { ContextSection } from "./ContextSection";
import { renderMarkdown, renderMessageWithMentions } from "../../utils/markdownUtils";
import { groupMessagesByTime } from "../../utils/messageUtils";

interface AssistantSidebarProps {
  className?: string;
  onMention?: (agent: Agent) => void;
}

export const AssistantSidebar: React.FC<AssistantSidebarProps> = ({
  className,
  onMention
}) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [availableApplications, setAvailableApplications] = useState<Application[]>([]);
  const hasLoadedAgentsRef = useRef(false);
  const hasLoadedApplicationsRef = useRef(false);
  const [currentMentions, setCurrentMentions] = useState<Agent[]>([]); // What's in the input box right now
  const [accumulatedContext, setAccumulatedContext] = useState<Agent[]>([]); // All agents mentioned in the conversation
  const [pendingChanges, setPendingChanges] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [isApproving, setIsApproving] = useState(false);
  const [selectedChanges, setSelectedChanges] = useState<Set<number>>(new Set());
  // For CREATE_AGENT
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [isNewApplicationModalOpen, setIsNewApplicationModalOpen] = useState(false);
  // For CREATE_ACTION
  const [actionFormData, setActionFormData] = useState<any>({});
  const [diffViewOpen, setDiffViewOpen] = useState(false);
  const [diffViewData, setDiffViewData] = useState<{
    title: string;
    original: string;
    proposed: string;
  } | null>(null);
  const [revertedMessageIds, setRevertedMessageIds] = useState<Set<string>>(new Set()); // Track which messages have been reverted
  const [pendingDecisionMarker, setPendingDecisionMarker] = useState<string | null>(null); // Track user decisions to prepend to next message
  const inputAreaRef = useRef<AssistantInputAreaRef>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { apiKey } = usePermissions();
  const { messages, addMessage, clearMessages, updateMessageSuggestions, toggleMessageExpanded } = useAssistant();

  const handleNewChat = () => {
    clearMessages();
    setCurrentMentions([]);
    setAccumulatedContext([]);
    setPendingChanges(null);
    setSelectedChanges(new Set());
    setRevertedMessageIds(new Set());
  };

  const messageGroups = groupMessagesByTime(messages);


  // Load available agents with their actions
  const loadAgents = (silent: boolean = false) => {
    if (apiKey) {
      // Use a no-op function for silent loading (doesn't show loading indicator)
      const loadingHandler = silent ? () => {} : setIsLoading;
      
      // Fetch agents with actions included
      AgentService.getAgents(loadingHandler, apiKey, true, true).subscribe({
        next: (response: any) => {
          // Handle both direct array and wrapped response formats
          const agentsList = Array.isArray(response) ? response : (response.agents || []);
          // Flatten agents and their actions into a single list for mentions
          const flattenedList: any[] = [];
          
          agentsList.forEach((agent: any) => {
            // Add the agent itself
            flattenedList.push({
              ...agent,
              isAction: false,
              displayName: agent.agent_name
            });
            
            // Add each action as "Agent Name/Action Name"
            if (agent.actions && Array.isArray(agent.actions)) {
              agent.actions.forEach((action: any) => {
                flattenedList.push({
                  ...action,
                  // Create a unique ID for the action item by combining agent_id and action.id
                  id: `${agent.id}_action_${action.id}`,
                  agent_id: agent.id,
                  agent_name: agent.agent_name,
                  action_id: action.id, // Store the original action ID
                  isAction: true,
                  displayName: `${agent.agent_name}/${action.action_name}`
                });
              });
            }
          });
          
          console.log("Flattened list with actions:", flattenedList.length, "entries");
          setAvailableAgents(flattenedList);
          hasLoadedAgentsRef.current = true;
        },
        error: (error) => {
          console.error("Failed to fetch agents:", error);
          if (!silent) setIsLoading(false);
        }
      });
    }
  };

  // Load available applications
  const loadApplications = (silent: boolean = true) => {
    if (apiKey) {
      if (hasLoadedApplicationsRef.current) {
        return;
      }
      const loadingHandler = silent ? () => {} : setIsLoading;
      ApplicationService.getApplications(loadingHandler, apiKey).subscribe({
        next: (response: any) => {
          const appsList = Array.isArray(response) ? response : (response.applications || []);
          console.log("Available applications loaded:", appsList.length);
          setAvailableApplications(appsList);
          hasLoadedApplicationsRef.current = true;
        },
        error: (error) => {
          console.error("Failed to fetch applications:", error);
        }
      });
    }
  };

  // Automatically add current page's agent to context
  useEffect(() => {
    // Check if we're on an agent page (/agents/{agentId})
    const agentIdMatch = location.pathname.match(/\/agents\/(\d+)/);
    if (agentIdMatch && availableAgents.length > 0) {
      const agentId = parseInt(agentIdMatch[1], 10);
      
      // Check if this agent is already in accumulated context
      const alreadyInContext = accumulatedContext.some(a => {
        // For regular agents, check id directly
        if (a.id === agentId) return true;
        // For actions, check agent_id
        if ((a as any).agent_id === agentId) return true;
        return false;
      });
      
      if (!alreadyInContext) {
        // Find the agent in available agents (not actions)
        const agent = availableAgents.find(a => {
          // Only match regular agents (not actions with string IDs)
          return a.id === agentId && !(a as any).isAction;
        });
        
        if (agent) {
          console.log(`Auto-adding agent ${agent.agent_name} to context from URL`);
          setAccumulatedContext(prev => [...prev, agent]);
        } else {
          console.log(`Agent with id ${agentId} not found in available agents`);
        }
      }
    }
  }, [location.pathname, availableAgents]);

  // Reload agents when they are updated, and update context if agent names changed
  useEffect(() => {
    const handleAgentUpdate = (event: any) => {
      console.log("Agent updated, reloading agent list for mentions...");
      if (!hasLoadedAgentsRef.current) {
        return;
      }
      loadAgents(true); // Silent reload (don't show loading indicator)
      
      // Update accumulated context with the latest agent details
      const affectedAgentIds = event.detail?.agentIds || [];
      if (affectedAgentIds.length > 0 && apiKey) {
        // Fetch updated details for agents in context silently
        AgentService.getAgents(() => {}, apiKey).subscribe({
          next: (response: any) => {
            const agentsList = Array.isArray(response) ? response : (response.agents || []);
            
            // Update accumulated context with new agent details
            setAccumulatedContext(prev => {
              return prev.map(contextAgent => {
                if (affectedAgentIds.includes(contextAgent.id)) {
                  // Find the updated agent details
                  const updatedAgent = agentsList.find((a: Agent) => a.id === contextAgent.id);
                  return updatedAgent || contextAgent;
                }
                return contextAgent;
              });
            });
            
            // Also update currentMentions if needed
            setCurrentMentions(prev => {
              return prev.map(mentionAgent => {
                if (affectedAgentIds.includes(mentionAgent.id)) {
                  const updatedAgent = agentsList.find((a: Agent) => a.id === mentionAgent.id);
                  return updatedAgent || mentionAgent;
                }
                return mentionAgent;
              });
            });
          },
          error: (error) => {
            console.error("Failed to fetch updated agent details:", error);
          }
        });
      }
    };

    window.addEventListener("agentUpdated", handleAgentUpdate);
    return () => {
      window.removeEventListener("agentUpdated", handleAgentUpdate);
    };
  }, [apiKey, accumulatedContext, currentMentions]);

  // Helper function to send system messages to LLM
  const sendSystemMessageToLLM = (systemMessage: string) => {
    // Collect ALL agents that have ever been mentioned in the conversation history
    const allHistoricalAgents = new Set<number>();
    
    // Add agents from all historical messages
    messages.forEach(msg => {
      msg.mentions.forEach(mention => {
        if ((mention as any).agent_id) {
          allHistoricalAgents.add((mention as any).agent_id);
        } else {
          allHistoricalAgents.add(mention.id);
        }
      });
    });
    
    // Add agents from current accumulated context
    accumulatedContext.forEach(agent => {
      if ((agent as any).agent_id) {
        allHistoricalAgents.add((agent as any).agent_id);
      } else {
        allHistoricalAgents.add(agent.id);
      }
    });
    
    const agentIds = Array.from(allHistoricalAgents);
    
    // Build conversation history including the system message
    const conversationHistory = messages.map(msg => ({
      role: msg.role || "user",
      content: msg.content
    }));
    
    setIsLoading(true);
    AgentService.sendAssistantMessage(systemMessage, agentIds, conversationHistory, setIsLoading, apiKey).subscribe({
      next: (response: any) => {
        console.log("Assistant response to system message:", response);
        
        // Handle all action types properly (same as regular handleSendMessage)
        if (response.action === "ANSWER") {
          addMessage(response.answer || "Got it!", [], "assistant");
        } else if (response.action === "EDIT" || response.action === "EDIT_AGENT" || response.action === "EDIT_ACTION") {
          const assistantMessage = response.explanation || "I've prepared some changes for you to review.";
          addMessage(assistantMessage, [], "assistant");
          
          // Only set pending changes if there are actual proposed changes
          if (response.proposed_changes && response.proposed_changes.length > 0) {
            setPendingChanges(response);
            setCurrentChangeIndex(0);
            // DON'T auto-open modal for EDIT responses from system messages
            // User should click "Review Suggestions" button to open modal
          }
        } else if (response.action === "CLARIFY") {
          const assistantMessage = response.question || "I need more information to help you with that.";
          addMessage(assistantMessage, [], "assistant");
        } else if (response.action === "CREATE_AGENT") {
          const assistantMessage = response.explanation || "I'll create this agent for you.";
          addMessage(assistantMessage, [], "assistant");
          
          // Only set pending changes if agent_data is actually provided
          if (response.agent_data && response.agent_data.agent_name && response.agent_data.description) {
            const pendingData = {
              action: "CREATE_AGENT",
              agent_data: response.agent_data,
              explanation: assistantMessage,
              next_step: response.next_step,
              hierarchy_tree: response.hierarchy_tree
            };
            setPendingChanges(pendingData);
            setIsModalOpen(true);
          } else {
            console.warn("[sendSystemMessageToLLM] CREATE_AGENT response missing required agent_data, ignoring");
          }
        } else if (response.action === "CREATE_ACTION") {
          const assistantMessage = response.explanation || "I'll add this capability for you.";
          addMessage(assistantMessage, [], "assistant");
          
          // Only set pending changes if action_data is actually provided and is an array
          if (response.action_data && Array.isArray(response.action_data) && response.action_data.length > 0) {
            const pendingData = {
              action: "CREATE_ACTION",
              action_data: response.action_data,
              explanation: assistantMessage,
              follow_up: response.follow_up,
              hierarchy_tree: response.hierarchy_tree
            };
            setPendingChanges(pendingData);
            
            // Initialize with all capabilities selected
            setSelectedChanges(new Set(response.action_data.map((_: any, i: number) => i)));
            
            setIsModalOpen(true);
          } else {
            console.warn("[sendSystemMessageToLLM] CREATE_ACTION response missing required action_data, ignoring");
          }
        } else if (response.action === "ADD_SUBAGENT") {
          const assistantMessage = response.explanation || "I'll link this subagent for you.";
          addMessage(assistantMessage, [], "assistant");
          
          // Only set pending changes if subagent_data is actually provided
          if (response.subagent_data && response.subagent_data.parent_agent_id && response.subagent_data.child_agent_id) {
            const pendingData = {
              action: "ADD_SUBAGENT",
              subagent_data: response.subagent_data,
              explanation: assistantMessage,
              next_step: response.next_step
            };
            setPendingChanges(pendingData);
            setIsModalOpen(true);
          } else {
            console.warn("[sendSystemMessageToLLM] ADD_SUBAGENT response missing required subagent_data, ignoring");
          }
        } else if (response.action === "MANAGE_ROUTING") {
          // Routing management request from system message
          const assistantMessage = response.explanation || "I'll manage the routing rules.";
          addMessage(assistantMessage, [], "assistant");
          
          // Store routing data for approval
          if (response.operation && (response.routing_rules || response.routing_rule)) {
            setPendingChanges(response);
            // DON'T auto-open modal for MANAGE_ROUTING responses from system messages
            // User should click the inline suggestion button to approve
          }
        } else {
          // Unknown action type, just acknowledge
          addMessage("Got it!", [], "assistant");
        }
        
        setIsLoading(false);
      },
      error: (error) => {
        console.error("Error sending system message to LLM:", error);
        setIsLoading(false);
      }
    });
  };

  const handleSendMessage = async (message: string, mentions: Agent[]) => {
    if (message.trim()) {
      // Mark any pending suggestions as ignored before sending new message
      let currentDecisionMarker = pendingDecisionMarker;
      const pendingMessage = messages.find(msg => 
        msg.suggestions && 
        msg.suggestionsStatus === "pending"
      );
      
      if (pendingMessage) {
        updateMessageSuggestions(pendingMessage.id, "ignored");
        // Set marker for ignored suggestions
        const changeCount = pendingMessage.suggestions?.proposed_changes?.length || 0;
        currentDecisionMarker = `[USER DECISION] User ignored the previous suggestion (${changeCount} change${changeCount !== 1 ? "s" : ""} not applied).`;
        setPendingDecisionMarker(null);
      }
      
      // Prepend decision marker if one exists
      let messageToSend = message;
      if (currentDecisionMarker) {
        messageToSend = `${currentDecisionMarker}\n\n${message}`;
        setPendingDecisionMarker(null);
      }
      
      // Add user message to chat immediately (without the marker - it's only for backend)
      addMessage(message, mentions);
      
      // Accumulate mentions in the overall conversation context
      const newAccumulatedContext = [...accumulatedContext];
      mentions.forEach(mention => {
        if (!newAccumulatedContext.find(m => m.id === mention.id)) {
          newAccumulatedContext.push(mention);
        }
      });
      setAccumulatedContext(newAccumulatedContext);
      
      // Collect ALL agents that have ever been mentioned in the conversation history
      // This ensures full context is maintained even if user removes agents from the Context list
      const allHistoricalAgents = new Set<number>();
      
      // Add agents from all historical messages
      messages.forEach(msg => {
        msg.mentions.forEach(mention => {
          // Extract the numeric agent_id for actions, or use id for regular agents
          if ((mention as any).agent_id) {
            allHistoricalAgents.add((mention as any).agent_id);
          } else {
            allHistoricalAgents.add(mention.id);
          }
        });
      });
      
      // Add agents from current accumulated context
      newAccumulatedContext.forEach(agent => {
        if ((agent as any).agent_id) {
          allHistoricalAgents.add((agent as any).agent_id);
        } else {
          allHistoricalAgents.add(agent.id);
        }
      });
      
      // Add agents from current mentions
      mentions.forEach(mention => {
        if ((mention as any).agent_id) {
          allHistoricalAgents.add((mention as any).agent_id);
        } else {
          allHistoricalAgents.add(mention.id);
        }
      });
      
      // Convert to array for API call
      const agentIds = Array.from(allHistoricalAgents);
      
      // Build conversation history for LLM context
      const conversationHistory = messages.map(msg => ({
        role: msg.role || "user",
        content: msg.content
      }));
      // Add the current message
      conversationHistory.push({
        role: "user",
        content: message
      });
      
      setIsLoading(true);
      AgentService.sendAssistantMessage(messageToSend, agentIds, conversationHistory, setIsLoading, apiKey).subscribe({
        next: (response: any) => {
          console.log("Assistant response:", response);
          
          // Handle different action types
          if (response.action === "ANSWER") {
            // Simple question - just show the answer
            addMessage(response.answer || "I've analyzed the agents.", [], "assistant");
          } else if (response.action === "EDIT" || response.action === "EDIT_AGENT" || response.action === "EDIT_ACTION") {
            // Edit request - show explanation and store changes for approval
            const assistantMessage = response.explanation || "I've prepared some changes for you to review.";
            
            // Store proposed changes for approval
            if (response.proposed_changes && response.proposed_changes.length > 0) {
              setPendingChanges(response);
              setCurrentChangeIndex(0);
              // Add message with suggestions
              addMessage(assistantMessage, [], "assistant", undefined, undefined, response);
            } else {
              addMessage(assistantMessage, [], "assistant");
            }
          } else if (response.action === "CLARIFY") {
            // LLM needs more information
            const assistantMessage = response.question || "I need more information to help you with that.";
            addMessage(assistantMessage, [], "assistant");
          } else if (response.action === "CREATE_AGENT") {
            // Agent creation request - show confirmation and creation data
            console.log("CREATE_AGENT response received:", response);
            console.log("agent_data:", response.agent_data);
            
            const assistantMessage = response.explanation || "I'll create this agent for you.";
            
            // Store creation data for confirmation
            const pendingData = {
              action: "CREATE_AGENT",
              agent_data: response.agent_data,
              explanation: assistantMessage,
              next_step: response.next_step
            };
            console.log("Setting pendingChanges:", pendingData);
            setPendingChanges(pendingData);
            
            // Add message with suggestions
            addMessage(assistantMessage, [], "assistant", undefined, undefined, response);
          } else if (response.action === "CREATE_ACTION") {
            // Action creation request - show confirmation and creation data
            const assistantMessage = response.explanation || "I'll add this capability for you.";
            
            // Store creation data for confirmation
            setPendingChanges({
              action: "CREATE_ACTION",
              action_data: response.action_data,
              explanation: assistantMessage,
              follow_up: response.follow_up
            });
            
            // Initialize with all capabilities selected
            if (Array.isArray(response.action_data)) {
              setSelectedChanges(new Set(response.action_data.map((_: any, i: number) => i)));
            }
            
            // Add message with suggestions
            addMessage(assistantMessage, [], "assistant", undefined, undefined, response);
          } else if (response.action === "ADD_SUBAGENT") {
            // Subagent relationship request - show confirmation
            console.log("ADD_SUBAGENT response received:", response);
            console.log("subagent_data:", response.subagent_data);
            
            const assistantMessage = response.explanation || "I'll link these agents together.";
            
            // Store creation data for confirmation
            setPendingChanges({
              action: "ADD_SUBAGENT",
              subagent_data: response.subagent_data,
              explanation: assistantMessage,
              next_step: response.next_step
            });
            
            // Add message with suggestions
            addMessage(assistantMessage, [], "assistant", undefined, undefined, response);
          } else if (response.action === "MANAGE_ROUTING") {
            // Routing management request - show explanation and store data for approval
            const assistantMessage = response.explanation || "I'll manage the routing rules.";
            
            // Store routing data for approval
            setPendingChanges(response);
            
            // Add message with suggestions
            addMessage(assistantMessage, [], "assistant", undefined, undefined, response);
          } else {
            // Fallback for other response types (ERROR, etc.)
            const assistantMessage = response.question || response.explanation || response.answer || response.error || "I've analyzed your request.";
            addMessage(assistantMessage, [], "assistant");
          }
          
          setIsLoading(false);
        },
        error: (error) => {
          console.error("Error sending message:", error);
          addMessage("I'm sorry, I encountered an error processing your request. Could you try again?", [], "assistant");
          setIsLoading(false);
        }
      });
    }
  };

  return (
    <div 
      className={cn("flex flex-col h-full", className)}
      style={{ overscrollBehavior: "contain" }}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/50 bg-gradient-to-b from-card/50 to-background backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Assistant</h2>
            <p className="text-xs text-muted-foreground">AI-powered agent management</p>
          </div>
          <Button
            onClick={handleNewChat}
            size="sm"
            variant="outline"
            className="h-9 gap-2 border-border/50 hover:border-maitai-green/50 hover:bg-maitai-green/5 transition-all"
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm font-medium">New Chat</span>
          </Button>
        </div>
      </div>

      {/* Messages area */}
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{ overscrollBehavior: "contain" }}
      >
        {messages.length === 0 ? (
          <EmptyState />
        ) : isLoading && messageGroups.length === 0 ? (
          <LoadingIndicator />
        ) : (
          <>
            {messageGroups.map((group, groupIdx) => (
              <div key={groupIdx} className="space-y-1">
                {/* Message bubbles in group */}
                {group.messages.map((msg, msgIdx) => (
                  <div key={msg.id} className="flex flex-col gap-2">
                    <MessageBubble
                      message={msg}
                      isLastInGroup={msg.id === group.messages[group.messages.length - 1].id}
                      renderMarkdown={renderMarkdown}
                      renderMessageWithMentions={renderMessageWithMentions}
                    />
                    
                    {/* Inline Suggestions */}
                    <InlineSuggestions
                      message={msg}
                      availableAgents={availableAgents}
                      availableApplications={availableApplications}
                      accumulatedContext={accumulatedContext}
                      apiKey={apiKey}
                      onAddMessage={addMessage}
                      onUpdateMessageSuggestions={updateMessageSuggestions}
                      onToggleMessageExpanded={toggleMessageExpanded}
                      onSetPendingChanges={setPendingChanges}
                      onSetSelectedChanges={setSelectedChanges}
                      onSetIsApproving={setIsApproving}
                      onSetDiffViewData={setDiffViewData}
                      onSetDiffViewOpen={setDiffViewOpen}
                      onLoadAgents={loadAgents}
                      onLoadApplications={loadApplications}
                      onAddToAccumulatedContext={(agent) => setAccumulatedContext(prev => [...prev, agent])}
                      pendingChanges={pendingChanges}
                      selectedChanges={selectedChanges}
                      isApproving={isApproving}
                      revertedMessageIds={revertedMessageIds}
                      onSetRevertedMessageIds={setRevertedMessageIds}
                      actionFormData={actionFormData}
                      onSetActionFormData={setActionFormData}
                      onSetPendingDecisionMarker={setPendingDecisionMarker}
                    />
                    
                  </div>
                ))}
                
                {/* Timestamp - only once per group */}
                <div className="flex justify-end">
                  <span className="text-xs text-muted-foreground mr-1">
                    {group.time}
                  </span>
                </div>
              </div>
            ))}
            
            {/* Loading indicator when messages exist and loading */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl bg-muted shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-maitai-green rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                      <div className="w-2 h-2 bg-maitai-green rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                      <div className="w-2 h-2 bg-maitai-green rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    </div>
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Context section */}
      <ContextSection
        accumulatedContext={accumulatedContext}
        currentMentions={currentMentions}
        onRemoveFromContext={(agentId) => setAccumulatedContext(prev => prev.filter(a => a.id !== agentId))}
        onRemoveFromCurrentMentions={(agentId) => setCurrentMentions(prev => prev.filter(a => a.id !== agentId))}
      />

      {/* Input area */}
      <AssistantInputArea
        ref={inputAreaRef}
        availableAgents={availableAgents}
        currentMentions={currentMentions}
        onSendMessage={handleSendMessage}
        onUpdateMentions={setCurrentMentions}
        isLoading={isLoading}
      />

      {/* Diff View for Viewing Proposed Changes */}
      {diffViewData && (
        <DiffView
          isOpen={diffViewOpen}
          onClose={() => {
            setDiffViewOpen(false);
            setDiffViewData(null);
          }}
          title={diffViewData.title}
          description="Viewing proposed changes in Diff mode"
          original={diffViewData.original}
          proposed={diffViewData.proposed}
        />
      )}
    </div>
  );
};
